import { ChangeDetectionStrategy, Component, effect, inject, untracked, ViewEncapsulation } from "@angular/core";
import { S25DropdownAbstract } from "../s25.dropdown.abstract";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { ApprovalGroup } from "../../approval-groups/service/approval.group.service";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { ApprovalGroupsStore } from "../../approval-groups/approval.groups.store";

@TypeManagerDecorator("s25-approval-group-dropdown")
@Component({
    selector: "s25-approval-group-dropdown",
    template: `
        @if (items) {
            <s25-generic-dropdown
                [value]="value()"
                [hasFav]="false"
                [onSelect]="onSelect"
                [items]="items"
                [staticItems]="staticItems"
                [autoOpen]="autoOpen"
                [alwaysOpen]="alwaysOpen"
                [hideSearch]="hideSearch"
                [focusFirst]="false"
                [searchEnabled]="true"
                [placeholder]="'Select an Approval Group'"
                [resetSelectedOnCleanup]="resetSelectedOnCleanup"
                [hasNoneItem]="hasNoneItem"
                [noneItemLabel]="noneItemLabel"
                (valueChange)="onValueChange($event)"
            />
        }
    `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class S25ApprovalGroupDropdownComponent extends S25DropdownAbstract {
    apGroupStore = inject(ApprovalGroupsStore);

    constructor() {
        super();
        effect(() => {
            const approvalGroups = this.apGroupStore.approvalGroups() ?? [];
            untracked(() => this.setItems(approvalGroups));
        });
    }

    setItems(approvalGroups: ApprovalGroup[]) {
        // Load approval groups and convert to DropDownItem format
        this.items = approvalGroups.map(
            (group) =>
                ({
                    itemId: group.approvalGroupId!,
                    itemName: group.name,
                    itemTypeId: undefined, // Approval groups don't have a specific type ID
                }) as DropDownItem,
        );

        // Sort by name
        this.items.sort((a, b) => (a.itemName || "").localeCompare(b.itemName || ""));

        // If we have a chosen value but it's not in the items (e.g., placeholder),
        // find the matching item and update the chosen value
        const value = this.value();
        if (value && value.itemId) {
            const matchingItem = this.items.find((item) => item.itemId === value.itemId);
            if (matchingItem) {
                this.value.set(matchingItem);
            }
        }

        this.cd.detectChanges();
    }
}
